import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AlertTriangle, History, Minus, Plus, X } from "lucide-react";

import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import Callout from "@/components/ui/Callout";
import { CenteredSpinner } from "@/components/shared/Loader";
import RetryButton from "@/components/shared/RetryButton";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { ComplianceService } from "@/services/complianceService";

/**
 * Immutable version history for one checklist (Phase 5F).
 *
 * **Read-only, and there is no Restore button.** That is a deliberate product
 * decision, not an omission: restoring a tree would silently move requirements
 * under every in-flight candidate, which is the exact failure the versioning
 * work exists to end. Re-creating an old shape is done through the builder,
 * where it goes through the re-apply wizard like any other edit.
 *
 * ## What each row answers
 *
 * The question that caused the original data-loss incident was "can I safely
 * edit this?", and nobody could answer it. So every version shows **In use (n)**
 * — how many candidates are pinned to it. A version with candidates on it is one
 * whose requirements are still live for real people.
 *
 * ## Accordion, latest expanded
 *
 * Collapsed by default so a long history stays scannable, with the newest
 * version open because that is the common case. Inside each version, sections
 * nest their documents and each document lists its required fields and weight,
 * plus a per-section weight total — a version whose weights do not sum to 100 is
 * exactly what someone auditing wants to see.
 *
 * ## Diff markers
 *
 * `+ / − / ~` against the PREVIOUS version, computed here from the two trees
 * rather than read from `changeSummary`. The summary describes the edit as a
 * whole; the markers have to sit on individual rows, and deriving them from the
 * trees keeps them correct even for the backfilled v1 (which has no summary).
 *
 * A backfilled version renders an explicit "reconstructed" note. It was rebuilt
 * from the checklist's state at migration time — the contents of earlier
 * versions were never recorded — and presenting a reconstruction as a recording
 * would be a lie the officer cannot detect.
 */

/** Stable key for a document across versions: its slot name, case-folded. */
const docKey = (name) => (name || "").trim().toLowerCase();

/** Sum of a section's document weights — surfaced so a broken total is visible. */
const sectionWeight = (section) => (section?.subsections || []).reduce((total, sub) => total + (Number(sub?.weightPercentage) || 0), 0);

/**
 * Per-row markers for one version against the one before it.
 *
 * Returns `{sections: {key: mark}, documents: {key: mark}}` where mark is
 * "added" | "changed" | undefined. Removals are reported separately per section
 * because a removed row has nothing left to hang a marker on — it is described
 * rather than drawn.
 */
const buildDiffMarks = (tree, previousTree) => {
  if (!previousTree) return { sections: {}, documents: {}, removed: [] };

  const prevSections = new Map((previousTree.sections || []).map((s) => [docKey(s.name), s]));
  const sections = {};
  const documents = {};
  const removed = [];

  (tree?.sections || []).forEach((section) => {
    const prevSection = prevSections.get(docKey(section.name));
    if (!prevSection) {
      sections[docKey(section.name)] = "added";
    }
    const prevDocs = new Map((prevSection?.subsections || []).map((d) => [docKey(d.name), d]));
    (section.subsections || []).forEach((doc) => {
      const prev = prevDocs.get(docKey(doc.name));
      if (!prev) {
        documents[docKey(doc.name)] = "added";
        return;
      }
      const fieldsNow = JSON.stringify((doc.requiredFields || []).map((f) => [f.fieldName, f.fieldType, f.isRequired]));
      const fieldsBefore = JSON.stringify((prev.requiredFields || []).map((f) => [f.fieldName, f.fieldType, f.isRequired]));
      if (fieldsNow !== fieldsBefore || Number(doc.weightPercentage) !== Number(prev.weightPercentage) || Boolean(doc.isRequired) !== Boolean(prev.isRequired)) {
        documents[docKey(doc.name)] = "changed";
      }
    });
    prevDocs.forEach((doc, key) => {
      if (!(section.subsections || []).some((d) => docKey(d.name) === key)) {
        removed.push(`${section.name} › ${doc.name}`);
      }
    });
  });

  prevSections.forEach((section, key) => {
    if (!(tree?.sections || []).some((s) => docKey(s.name) === key)) {
      removed.push(section.name);
    }
  });

  return { sections, documents, removed };
};

const MARK_STYLE = {
  added: { label: "+", className: "text-tone-emerald", title: "Added in this version" },
  changed: { label: "~", className: "text-tone-amber", title: "Changed in this version" },
};

const DiffMark = ({ mark }) => {
  const style = MARK_STYLE[mark];
  if (!style) return <span className="w-3 shrink-0" aria-hidden="true" />;
  return (
    <span className={`w-3 shrink-0 font-mono text-[13px] font-bold ${style.className}`} title={style.title}>
      {style.label}
    </span>
  );
};

/** `+2 added` `−1 removed` `~1 changed`, from the stored change summary. */
const ChangeChips = ({ counts }) => {
  if (!counts) return null;
  const chips = [
    counts.added ? { key: "a", text: `+${counts.added} added`, cls: "bg-tone-emerald-soft text-tone-emerald" } : null,
    counts.removed ? { key: "r", text: `−${counts.removed} removed`, cls: "bg-tone-rose-soft text-tone-rose" } : null,
    counts.changed ? { key: "c", text: `~${counts.changed} changed`, cls: "bg-tone-amber-soft text-tone-amber" } : null,
  ].filter(Boolean);
  if (!chips.length) return null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {chips.map((chip) => (
        <span key={chip.key} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${chip.cls}`}>
          {chip.text}
        </span>
      ))}
    </span>
  );
};

const VersionTree = ({ tree, marks, showDiff }) => {
  const sections = tree?.sections || [];
  if (!sections.length) {
    return <p className="px-4 py-3 text-[12px] text-text-secondary">This version contained no documents.</p>;
  }
  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      {sections.map((section) => {
        const total = sectionWeight(section);
        const mark = showDiff ? marks.sections[docKey(section.name)] : undefined;
        return (
          <div key={section.name} className="rounded-lg border border-border-primary/60 bg-background-form/30">
            <div className="flex items-center justify-between gap-3 border-b border-border-primary/50 px-3 py-2">
              <div>
                <span className="truncate text-[13px] font-medium text-text-primary">{section.name}</span>
              </div>
              {/* A total that is not 100 is the thing an auditor is
                                looking for, so it is stated rather than hidden. */}
              <span
                className={`shrink-0 text-[11px] font-medium ${total === 100 ? "text-text-secondary" : "text-tone-amber"}`}
                title={total === 100 ? undefined : "Weights do not total 100%"}
              >
                {total}%
              </span>
            </div>

            <div className="flex flex-col divide-y divide-border-primary/40">
              {(section.subsections || []).map((doc) => (
                <div key={doc.name} className="px-3 py-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="truncate text-[12.5px] text-text-primary">{doc.name}</span>
                      {doc.isRequired === false && <span className="shrink-0 rounded bg-background-icon px-1.5 py-0.5 text-[10px] text-text-secondary">Optional</span>}
                    </div>
                    <span className="shrink-0 text-[11px] text-text-secondary">{Number(doc.weightPercentage) || 0}%</span>
                  </div>
                  {(doc.requiredFields || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1 pl-[18px]">
                      {doc.requiredFields.map((field) => (
                        <span
                          key={field.fieldName}
                          className="rounded bg-background-icon px-1.5 py-0.5 text-[10.5px] text-text-secondary"
                          title={`${field.fieldName} · ${field.fieldType}`}
                        >
                          {field.fieldName}
                          <span className="opacity-60"> · {field.fieldType}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {showDiff && marks.removed.length > 0 && (
        <div className="rounded-lg border border-tone-rose/30 bg-tone-rose-soft/40 px-3 py-2">
          <p className="text-[11.5px] font-medium text-tone-rose">Removed in this version</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {marks.removed.map((label) => (
              <li key={label} className="text-[11.5px] text-text-secondary">
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ChecklistVersionsModal = ({ open, onClose, checklistId, checklistName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(null);
  // Which versions are expanded. Seeded with the latest on load, so the
  // common case needs no click and a long history still stays scannable.
  const [expanded, setExpanded] = useState(() => new Set());
  const [showDiff, setShowDiff] = useState(true);

  const load = useCallback(async () => {
    if (!checklistId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ComplianceService.getChecklistVersions(checklistId);
      const data = res?.history ?? null;
      setHistory(data);
      const latest = data?.versions?.[0]?.versionNumber;
      setExpanded(latest != null ? new Set([latest]) : new Set());
    } catch (e) {
      setError(apiErrorMessageFormat(e, "Failed to load checklist history"));
    } finally {
      setLoading(false);
    }
  }, [checklistId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Each version is diffed against the NEXT one in the list, which is the
  // previous version — the API returns newest-first.
  const marksByVersion = useMemo(() => {
    const versions = history?.versions || [];
    const out = {};
    versions.forEach((version, index) => {
      const previous = versions[index + 1];
      out[version.versionNumber] = buildDiffMarks(version.tree, previous?.tree);
    });
    return out;
  }, [history]);

  if (!open) return null;

  const versions = history?.versions || [];

  const toggle = (versionNumber) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(versionNumber)) next.delete(versionNumber);
      else next.add(versionNumber);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-xl border border-border-primary bg-background-secondary shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-border-primary px-5 py-4">
          <div className="flex min-w-0 items-start gap-2.5">
            <History size={17} className="mt-0.5 shrink-0 text-text-secondary" />
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold text-text-primary">Checklist history</h2>
              <p className="truncate text-[12px] text-text-secondary">
                {checklistName || history?.checklistName}
                {history?.currentVersion != null && ` · currently v${history.currentVersion}`}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {versions.length > 1 && (
              <label className="flex cursor-pointer items-center gap-1.5 text-[11.5px] text-text-secondary">
                <Checkbox checked={showDiff} onCheckedChange={(value) => setShowDiff(!!value)} />
                Show changes
              </label>
            )}
            <ButtonShadcn variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="h-auto w-auto rounded-full p-1 text-text-secondary hover:bg-background-icon">
              <X size={16} />
            </ButtonShadcn>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading && <CenteredSpinner />}

          {!loading && error && (
            <div className="flex flex-col items-start gap-3 py-6">
              <p className="text-[13px] text-tone-rose">{error}</p>
              <RetryButton onClick={load} />
            </div>
          )}

          {!loading && !error && versions.length === 0 && <p className="py-8 text-center text-[13px] text-text-secondary">No recorded versions yet.</p>}

          {/* Versions that predate the history table. The counter has
                        always incremented on every edit, but nothing recorded
                        what those versions CONTAINED — so a checklist edited
                        twice before this shipped sits at v3 with one snapshot
                        numbered 3. Saying so is the difference between "the
                        history starts here" and "two versions were lost". */}
          {!loading && !error && history?.unrecordedVersionsBefore > 0 && (
            <div className="mb-2.5 rounded-lg border border-border-primary/60 bg-background-form/30 px-3 py-2">
              <p className="text-[11.5px] text-text-secondary">
                {history.unrecordedVersionsBefore === 1 ? "One earlier version" : `${history.unrecordedVersionsBefore} earlier versions`} of this checklist{" "}
                {history.unrecordedVersionsBefore === 1 ? "exists" : "exist"} but {history.unrecordedVersionsBefore === 1 ? "was" : "were"} never recorded — this
                checklist was edited before version history was introduced. Everything from{" "}
                <strong className="font-semibold text-text-primary">v{versions[versions.length - 1]?.versionNumber}</strong> onward is recorded in full.
              </p>
            </div>
          )}

          {!loading && !error && versions.length > 0 && (
            <div className="flex flex-col gap-2.5">
              {versions.map((version) => {
                const isOpen = expanded.has(version.versionNumber);
                const marks = marksByVersion[version.versionNumber] || {
                  sections: {},
                  documents: {},
                  removed: [],
                };
                return (
                  <div key={version.id} className="overflow-hidden rounded-lg border border-border-primary bg-background-form/20">
                    <ButtonShadcn
                      variant="ghost"
                      onClick={() => toggle(version.versionNumber)}
                      aria-expanded={isOpen}
                      className="h-auto w-full items-center justify-between gap-3 rounded-none px-4 py-3 text-left hover:bg-background-icon/40"
                    >
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="shrink-0 text-[13px] font-semibold text-text-primary">v{version.versionNumber}</span>
                        <span className="truncate text-[11.5px] text-text-secondary">
                          {version.createdAt
                            ? new Date(version.createdAt).toLocaleString(undefined, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </span>
                        <span className="truncate text-[11.5px] text-text-secondary">· {version.createdBy}</span>
                        {version.isCurrent && <span className="shrink-0 rounded bg-tone-blue-soft px-1.5 py-0.5 text-[10px] font-semibold text-tone-blue">Current</span>}
                        {/* The question that caused the incident:
                                                    is anyone actually on this version? */}
                        {version.inUseCount > 0 && (
                          <span
                            className="shrink-0 rounded bg-tone-violet-soft px-1.5 py-0.5 text-[10px] font-semibold text-tone-violet"
                            title={`${version.inUseCount} candidate(s) are pinned to this version`}
                          >
                            In use ({version.inUseCount})
                          </span>
                        )}
                        <ChangeChips counts={version.changeSummary?.counts} />
                      </div>
                      {isOpen ? <Minus size={14} className="shrink-0 text-text-secondary" /> : <Plus size={14} className="shrink-0 text-text-secondary" />}
                    </ButtonShadcn>

                    {isOpen && (
                      <>
                        {version.backfilled && (
                          <div className="px-4 pb-2">
                            <Callout tone="neutral" icon={AlertTriangle} className="rounded-lg px-3 py-2 text-[11.5px]">
                              Reconstructed from the checklist as it stood when version history was introduced. Earlier versions were never recorded.
                            </Callout>
                          </div>
                        )}
                        <VersionTree tree={version.tree} marks={marks} showDiff={showDiff && !version.backfilled} />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border-primary px-5 py-3">
          <p className="text-[11px] text-text-secondary">History is read-only — versions cannot be restored.</p>
          <Button onClick={onClose} className="rounded-full bg-background-icon px-4 py-2 text-[11px] text-text-primary hover:bg-background-icon/60">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistVersionsModal;
